import React, { useState, useEffect, useRef, useCallback } from 'react'
import Modal from 'react-responsive-modal'
import styles from './styles.module.css'
import Button from '../../components/Button'
import Confirmation from '../../components/Confirmation/Confirmation'
import { personnelModalModes } from '../../assets/const'
import cn from 'classnames'
import { Main, Additional, Education, Qualification, Award, Punish, Experience, Lessons, Events, Documents, Attestation, Roles, Family, Vacation, SocialBenefits, MilitaryRecord, Languages, IssuedDocuments, UmbInfo } from './ModalTabs'
import { StaffRosterContext } from '../../utils/context'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import {
    createPersonnelInfo,
    updatePersonnelInfo,
    getPersonTable,
    addDeletion,
    clearDeletions,
    TAB_MAPPING
} from '../../actions/personnel/personnel'
import {
    getPersonnelPhoto,
    uploadPersonnelPhoto,
    deletePersonnelPhoto,
} from '../../actions/personnel/photo'
import PasswordModal from './PasswordModal'
import { createNotification } from '../../components/Notifications/Notifications'

const PHOTO_MAX_SIZE = 5 * 1024 * 1024
const PHOTO_ALLOWED_TYPES = ['image/jpeg', 'image/png']


export default function ModalComponent({ isOpen, closeModal, modalOpen, personnelId, }) {

    const dispatch = useDispatch()

    const [mode, setMode] = useState('main')
    const [passwordModal, setPasswordModal] = useState(false)
    const [showValidationWarning, setShowValidationWarning] = useState(false)
    const [validationMessage, setValidationMessage] = useState('')
    const [confirmClose, setConfirmClose] = useState(false)
    const addMode = modalOpen === 'add' ? true : false

    // Получаем данные из Redux
    const { personTabData, deletionsTracker } = useSelector(state => state.personnel)

    // Отслеживание загруженных табов (чтобы не перезагружать при повторном переходе)
    const loadedTabsRef = useRef(new Set())

    const photoInputRef = useRef(null)
    const [photoUrl, setPhotoUrl] = useState(null)
    const [photoLoading, setPhotoLoading] = useState(false)
    const [isDraggingPhoto, setIsDraggingPhoto] = useState(false)

    // Загрузка данных при смене таба
    const handleTabChange = (newMode) => {
        setMode(newMode)
        // Загружаем данные таба только в режиме редактирования и если таб ещё не загружен
        if (!addMode && personnelId && !loadedTabsRef.current.has(newMode)) {
            loadedTabsRef.current.add(newMode)
            dispatch(getPersonTable(newMode, personnelId))
        }
    }

    // Загрузка данных main таба при открытии модалки
    useEffect(() => {
        if (isOpen) {
            // Сбрасываем список загруженных табов при открытии модалки
            loadedTabsRef.current = new Set()
            // main таб уже загружен в index.jsx перед открытием модалки
            if (!addMode && personnelId) {
                loadedTabsRef.current.add('main')
            }
        }
    }, [isOpen, personnelId, addMode])

    // Сброс на вкладку main при открытии в режиме добавления
    useEffect(() => {
        if (isOpen && addMode) {
            setMode('main')
        }
    }, [isOpen, addMode])

    // Загрузка фото сотрудника (GET /personnel_structure/{mid}/photo)
    const loadPhoto = useCallback(async () => {
        if (!personnelId) {
            setPhotoUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null })
            setPhotoLoading(false)
            return
        }
        setPhotoLoading(true)
        const blob = await dispatch(getPersonnelPhoto(personnelId))
        setPhotoUrl(prev => {
            if (prev) URL.revokeObjectURL(prev)
            return blob ? URL.createObjectURL(blob) : null
        })
        setPhotoLoading(false)
    }, [personnelId, dispatch])

    useEffect(() => {
        if (isOpen && !addMode && personnelId) {
            loadPhoto()
        } else {
            setPhotoUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null })
            setPhotoLoading(false)
        }
    }, [isOpen, personnelId, addMode]) // eslint-disable-line

    const handlePhotoSelect = async (file) => {
        if (!file || !personnelId) return
        if (!PHOTO_ALLOWED_TYPES.includes(file.type)) {
            createNotification('error', 'Поддерживаются только файлы JPG и PNG')
            return
        }
        if (file.size > PHOTO_MAX_SIZE) {
            createNotification('error', 'Максимальный размер файла — 5 МБ')
            return
        }
        const success = await dispatch(uploadPersonnelPhoto(personnelId, file))
        if (success) loadPhoto()
    }

    const handlePhotoInputChange = (e) => {
        const file = e.target.files[0]
        e.target.value = ''
        handlePhotoSelect(file)
    }

    const handlePhotoDrop = (e) => {
        e.preventDefault()
        setIsDraggingPhoto(false)
        handlePhotoSelect(e.dataTransfer.files[0])
    }

    const handlePhotoDeleteClick = async () => {
        const success = await dispatch(deletePersonnelPhoto(personnelId))
        if (success) loadPhoto()
    }

    // Создание сотрудника
    const handleCreate = async (values) => {
        const success = await dispatch(createPersonnelInfo(values))
        if (success) {
            closeModal()
        }
    }

    // Обновление сотрудника - собираем только изменённые данные по каждой вкладке
    const handleUpdate = async (values) => {
        const initialValues = initialValuesRef.current
        let payload = {}

        // Хелпер для очистки временных id в массивах
        const cleanArrayData = (arr) => {
            if (!Array.isArray(arr) || !arr.length) return []
            return arr.map(item => {
                if (String(item.id || item.xp_key).startsWith('new_')) {
                    const { id, ...rest } = item
                    return rest
                }
                return item
            })
        }

        // Словарные вкладки - собираем только изменённые поля
        const dictTabs = ['main', 'additional', 'military']
        dictTabs.forEach(tab => {
            const dirtyFields = getDirtyFields(values[tab], initialValues[tab])
            if (dirtyFields) {
                payload[tab] = dirtyFields
            }
        })

        // Массивные вкладки - отправляем если были изменения
        const arrayTabs = [
            'education',
            'professional_development',
            'awards',
            'penalties',
            'certification',
            'family',
            'vacation',
            'privileges',
            'foreign_languages',
            'equipment'
        ]

        arrayTabs.forEach(tab => {
            const currentData = values[tab]
            const initialData = initialValues[tab]
            // Сравниваем массивы
            if (JSON.stringify(currentData) !== JSON.stringify(initialData)) {
                if (Array.isArray(currentData) && currentData.length) {
                    payload[tab] = cleanArrayData(currentData)
                }
            }
        })

        // Composite данные (total_work_experience)
        const expCurrent = values.total_work_experience
        const expInitial = initialValues.total_work_experience
        if (JSON.stringify(expCurrent) !== JSON.stringify(expInitial)) {
            if (expCurrent?.items?.length || expCurrent?.summary) {
                payload.total_work_experience = {
                    items: cleanArrayData(expCurrent?.items || []),
                    summary: expCurrent?.summary || {}
                }
            }
        }

        // Добавляем поля удаления из трекера
        Object.keys(deletionsTracker || {}).forEach(apiTab => {
            if (deletionsTracker[apiTab] && deletionsTracker[apiTab].length > 0) {
                payload[`del_${apiTab}`] = deletionsTracker[apiTab]
            }
        })

        // Отправляем только если есть изменения
        if (Object.keys(payload).length === 0) {
            return
        }

        const success = await dispatch(updatePersonnelInfo(personnelId, payload))
        if (success) {
            dispatch(clearDeletions())
            // Обновляем начальные значения после успешного сохранения
            // чтобы dirty tracking работал корректно для следующих изменений
            Object.keys(payload).forEach(tab => {
                if (!tab.startsWith('del_')) {
                    initialValuesRef.current[tab] = JSON.parse(JSON.stringify(values[tab]))
                }
            })
            // Перезагружаем данные текущей вкладки
            if (personnelId) {
                dispatch(getPersonTable(mode, personnelId))
            }
        }
    }

    // Трекинг удаления записи
    const trackDeletion = (apiTab, itemId) => {
        dispatch(addDeletion(apiTab, itemId))
    }

    // Начальные значения для Formik - структурированы по вкладкам согласно API
    const getInitialValues = () => {
        return {
            // Словарные вкладки (объекты)
            main: {},
            additional: {},
            military: {},
            // Массивные вкладки
            education: [],
            professional_development: [],
            awards: [],
            penalties: [],
            certification: [],
            family: [],
            vacation: [],
            privileges: [],
            foreign_languages: [],
            total_work_experience: { items: [], summary: {} },
            equipment: []
        }
    }

    // Хелпер для получения только изменённых полей объекта
    const getDirtyFields = (current, initial) => {
        if (!current || typeof current !== 'object') return current
        if (Array.isArray(current)) return current

        const dirty = {}
        Object.keys(current).forEach(key => {
            const currentVal = current[key]
            const initialVal = initial?.[key]
            // Сравниваем значения (приводим к строке для корректного сравнения)
            if (JSON.stringify(currentVal) !== JSON.stringify(initialVal)) {
                dirty[key] = currentVal
            }
        })
        return Object.keys(dirty).length > 0 ? dirty : null
    }

    // Ref для хранения начальных значений (для сравнения при сохранении)
    const initialValuesRef = useRef({})

    // Функция для обновления начальных значений (вызывается из вкладок при загрузке данных)
    const updateInitialValues = useCallback((tab, data) => {
        initialValuesRef.current = {
            ...initialValuesRef.current,
            [tab]: JSON.parse(JSON.stringify(data)) // глубокое копирование
        }
    }, [])

    const allForms = useFormik({
        initialValues: getInitialValues(),
        enableReinitialize: true,
        onSubmit: addMode ? handleCreate : handleUpdate
    })

    const handleClose = useCallback(() => {
        if (allForms?.dirty) setConfirmClose(true); else closeModal()
    }, [allForms.dirty, closeModal])

    const acceptConfirmClose = useCallback(() => { closeModal(); setConfirmClose(false) }, [closeModal])
    const rejectConfirmClose = useCallback(() => setConfirmClose(false), [])

    // Проверка заполненности обязательных полей
    const areRequiredFieldsFilled = () => {
        const { values } = allForms
        const mainValues = values.main || {}
        const additionalValues = values.additional || {}
        return mainValues.lastname?.trim() && mainValues.birthday && additionalValues.email?.trim()
    }

    // Валидация и сохранение
    const handleSaveClick = () => {
        const { values } = allForms
        const mainValues = values.main || {}
        const additionalValues = values.additional || {}

        if (addMode) {
            if (!mainValues.lastname?.trim()) {
                setValidationMessage('Заполните поле Фамилия во вкладке Главная')
                setShowValidationWarning(true)
                return
            }
            if (!mainValues.birthday) {
                setValidationMessage('Заполните поле Дата рождения во вкладке Главная')
                setShowValidationWarning(true)
                return
            }
            if (!additionalValues.email?.trim()) {
                setValidationMessage('Заполните поле E-mail во вкладке Дополнительно')
                setShowValidationWarning(true)
                return
            }
        }

        allForms.handleSubmit()
    }

    const renderLi = ({ name, value, icon }) => {
        const selected = value === mode
        const isDisabled = addMode && !areRequiredFieldsFilled() &&
            value !== 'main' && value !== 'additional'

        return (
            <li
                key={value}
                onClick={() => !isDisabled && handleTabChange(value)}
                className={cn(styles.modalMenuItem, {
                    [styles.selectedMenuItem]: selected,
                    [styles.disabledMenuItem]: isDisabled
                })}
            >
                {icon()}
                {name}
            </li>
        )
    }

    const renderTab = () => {
        switch (mode) {
            case 'main': return <Main />
            case 'additional': return <Additional />
            case 'education': return <Education />
            case 'qualification': return <Qualification table />
            case 'award': return <Award />
            case 'punish': return <Punish />
            case 'experience': return <Experience />
            case 'lesson': return <Lessons />
            case 'event': return <Events />
            case 'role': return <Roles />
            case 'documents': return <Documents />
            case 'attestation': return <Attestation />
            case 'family': return <Family />
            case 'vacation': return <Vacation />
            case 'socialBenefits': return <SocialBenefits />
            case 'militaryRecord': return <MilitaryRecord />
            case 'languages': return <Languages />
            case 'issuedDocuments': return <IssuedDocuments />
            case 'umbInfo': return <UmbInfo />
            default: return
        }
    }

    return (
        <>
            <StaffRosterContext.Provider value={{
                closeModal,
                allForms,
                addMode,
                personTabData,
                trackDeletion,
                deletionsTracker,
                currentTab: mode,
                mid: personnelId,
                updateInitialValues
            }}>
                {showValidationWarning && (
                    <Confirmation
                        title={validationMessage}
                        acceptConfirmation={() => setShowValidationWarning(false)}
                        rejectConfirmation={() => setShowValidationWarning(false)}
                    />
                )}
                <Modal
                    open={isOpen}
                    onClose={handleClose}
                    classNames={{ modal: styles.modalMain }}
                    closeOnOverlayClick={true}
                    closeOnEsc={true}
                    showCloseIcon={false}
                    animationDuration={1}
                >
                    <div className={styles.modalContent}>
                        <div className={styles.modalMenu}>
                            <div className={styles.photoSidebar}>
                                <input
                                    ref={photoInputRef}
                                    type='file'
                                    accept='image/jpeg,image/png'
                                    hidden
                                    onChange={handlePhotoInputChange}
                                />
                                {photoLoading ? (
                                <div className={styles.photoSidebarBox} />
                                ) : photoUrl ? (
                                <>
                                <div
                                    className={cn(styles.photoSidebarBox, { [styles.photoSidebarBoxActive]: isDraggingPhoto })}
                                    onClick={() => personnelId && photoInputRef.current.click()}
                                    onDragOver={(e) => { e.preventDefault(); setIsDraggingPhoto(true) }}
                                    onDragLeave={(e) => { e.preventDefault(); setIsDraggingPhoto(false) }}
                                    onDrop={handlePhotoDrop}
                                >
                                    <img src={photoUrl} alt='' className={styles.photoSidebarImage} />
                                    <div className={styles.photoSidebarOverlay}>
                                                <button type='button' className={styles.photoSidebarBtn} onClick={(e) => { e.stopPropagation(); photoInputRef.current.click() }}>Заменить</button>
                                                <button type='button' className={cn(styles.photoSidebarBtn, styles.photoSidebarBtnDanger)} onClick={(e) => { e.stopPropagation(); handlePhotoDeleteClick() }}>Удалить</button>
                                    </div>
                                </div>
                                <div className={styles.photoSidebarCaption}>Наведите на фото — заменить или удалить</div>
                                </>
                                ) : (
                                <>
                                <div
                                    className={cn(styles.photoDropzone, { [styles.photoDropzoneActive]: isDraggingPhoto })}
                                    onClick={() => personnelId && photoInputRef.current.click()}
                                    onDragOver={(e) => { e.preventDefault(); setIsDraggingPhoto(true) }}
                                    onDragLeave={(e) => { e.preventDefault(); setIsDraggingPhoto(false) }}
                                    onDrop={handlePhotoDrop}
                                >
                                    <div className={styles.photoIcon}>&#128247;</div>
                                    <div className={styles.photoDropzoneLabel}>фото</div>
                                    <div className={styles.photoDropzoneHint}>Нажмите или перетащите файл</div>
                                </div>
                                <button
                                    type='button'
                                    className={styles.photoUploadButton}
                                    disabled={!personnelId}
                                    onClick={() => photoInputRef.current.click()}
                                >
                                    Загрузить фото
                                </button>
                                <div className={styles.photoSidebarCaption}>JPG, PNG · до 5 МБ</div>
                                </>
                                )}
                            </div>
                            <ul>{personnelModalModes.map(renderLi)}</ul>
                        </div>
                        <div className={styles.modalMain}>{renderTab()}</div>
                        <div className={styles.modalFooter}>
                            <Button mode='bordered' onClick={() => setPasswordModal(true)}>Пароль</Button>
                            <div className={styles.modalFooterRightBlock}>
                                <div>
                                    {/* <div>Создал: Алехина Милана Дмитриевна 21.05.2022 15:34</div>
                        <div>Изменил: Алехина Милана Дмитриевна 21.05.2022 15:34</div> */}
                                </div>
                                <Button mode='white' onClick={handleSaveClick}>Сохранить</Button>
                                <Button mode='white' onClick={closeModal}>Закрыть</Button>
                            </div>
                        </div>
                    </div>
                    {confirmClose && (
                        <Confirmation
                            title="Закрыть без сохранения изменений?"
                            acceptConfirmation={acceptConfirmClose}
                            rejectConfirmation={rejectConfirmClose}
                        />
                    )}
                </Modal>
            </StaffRosterContext.Provider>
            <PasswordModal
                isOpen={passwordModal}
                mid={personnelId}
                onClose={() => setPasswordModal(false)}
            />
        </>
    )
}
